import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter } from '@angular/material/core';

import { NotificationService } from '@shared/services/notification.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { DivineServiceFacadeService } from '../../services/divine-service-facade.service';
import { DivineServiceFormBuilderService } from '../../services/divine-service-form-builder.service';
import { DivineServiceFormComponent } from '../../components/divine-service-form/divine-service-form.component';
import { DivineServiceApiService } from '../../services/api/divine-service-api.service';
import { DivineServiceState } from '../../models/divine-service-state.model';
import { DivineServiceDetailDto } from '../../models/divine-service.model';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { UnitSelectorSource } from '@shared/components/unit-selector/unit-selector.component';
import { getApiErrorMessage } from '@core/http/api-error-message.util';

/**
 * Página para crear o editar un servicio divino
 * Soporta tanto creación (new) como edición de servicios existentes
 * Usa el patrón Facade + State para gestionar el flujo
 */
@Component({
  selector: 'app-divine-service-edit',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCardModule,
    DivineServiceFormComponent
  ],
  templateUrl: './divine-service-edit.component.html',
  styleUrl: './divine-service-edit.component.scss'
})
export class DivineServiceEditComponent implements OnInit, OnDestroy {
  // Observable del estado centralizado
  state$: Observable<DivineServiceState>;
  serviceTypes$: Observable<any>;
  visibility$: Observable<any>;

  serviceForm!: FormGroup;

  /** Fuente de unidades para el selector. Siempre se limita a comunidades manejables. */
  unitSource: UnitSelectorSource = 'all';

  /** true mientras se verifican los permisos del usuario; oculta el formulario hasta resolverse */
  isLoadingPermissions = true;

  private serviceId: string | null = null;
  private isNewService = true;
  private unitId: string | null = null;
  private destroy$ = new Subject<void>();
  private readonly COMMUNITY_LEVEL = 5;

  constructor(
    private cdr: ChangeDetectorRef,
    private facade: DivineServiceFacadeService,
    private formBuilderService: DivineServiceFormBuilderService,
    private divineServiceApiService: DivineServiceApiService,
    private route: ActivatedRoute,
    private dateAdapter: DateAdapter<any>,
    private router: Router,
    private notificationService: NotificationService,
    private breadcrumbService: BreadcrumbService
  ) {
    this.serviceForm = this.formBuilderService.createServiceForm();
    this.state$ = this.facade.state$;
    this.serviceTypes$ = this.facade.serviceTypes$;
    this.visibility$ = this.facade.visibility$;
  }

  ngOnInit(): void {
    // Leer unit-id del árbol de rutas padre
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    this.unitId = allParams['unit-id'] ?? null;

    // Manejar parámetros de ruta
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['divine-service-id'] === 'new') {
          this.isNewService = true;
          this.serviceId = null;
          this.initializeNewService();
        } else if (params['divine-service-id']) {
          this.isNewService = false;
          this.serviceId = params['divine-service-id'];
          this.loadExistingService(params['divine-service-id']);
        } else {
          this.isNewService = true;
          this.serviceId = null;
          this.initializeNewService();
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Sincroniza el formulario con los datos del servicio cargado
   */
  private syncFormWithLoadedService(service: DivineServiceDetailDto): void {
    this.serviceForm.patchValue({
      date: this.fromDateTime(service.startAt),
      type: service.divineServiceTypeId,
      bibleText: service.bibleText,
      memberInCharge: service.memberInCharge,
      location: service.location,
      organizationUnitId: service.organizationUnitId,
      totalMinors: service.totalMinors,
      totalAdults: service.totalAdults,
      notes: service.notes,
      offeringsARS: service.offeringsARS,
      offeringsUSD: service.offeringsUSD,
      visitsAdults: (service as any)?.visitsAdults || 0,
      visitsMinors: (service as any)?.visitsMinors || 0,
      newAdults: (service as any)?.newAdults || 0,
      newMinors: (service as any)?.newMinors || 0
    }, { emitEvent: false });
  }

  /**
   * Carga un servicio divino existente para edición.
   * Verifica que el usuario tenga permisos sobre la unidad del servicio
   * antes de mostrar el formulario.
   */
  private loadExistingService(id: string): void {
    this.isLoadingPermissions = true;
    this.cdr.markForCheck();

    this.divineServiceApiService.getManageableUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dtos) => {
          const manageableUnits: OrganizationUnitItem[] = dtos
            .filter(dto => dto.id != null && dto.name != null)
            .filter(dto => dto.organizationLevelId === this.COMMUNITY_LEVEL)
            .map(dto => ({
              id: dto.id!,
              name: dto.name!,
              levelId: dto.organizationLevelId ?? 0,
              levelName: dto.levelName ?? 'Sin nivel',
              parentId: dto.parentUnitId ?? undefined,
              ancestorNames: dto.ancestorNames ?? []
            }));

          // En edición: cargar el SD primero para conocer su unidad, luego verificar permisos
          this.facade.loadDivineService(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (service) => {
                const hasPermission = manageableUnits.some(u => u.id === service.organizationUnitId);
                if (!hasPermission) {
                  this.notificationService.error('No tenés permisos para editar este servicio divino.');
                  this.router.navigate(['/divine-services']);
                  return;
                }

                this.unitSource = manageableUnits;
                this.isLoadingPermissions = false;
                this.syncFormWithLoadedService(service);
                const serviceDate = new Date(service.startAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                this.breadcrumbService.setDynamicCrumb('divine-service-id', serviceDate);
                if (service.organizationUnitName) {
                  this.breadcrumbService.setDynamicCrumb('unit-id', service.organizationUnitName);
                }
                if (manageableUnits.length === 1) {
                  this.serviceForm.get('organizationUnitId')?.disable();
                }
                this.cdr.markForCheck();
              },
              error: (error) => {
                console.error('Error loading divine service:', error);
                this.notificationService.error(getApiErrorMessage(
                  error,
                  'No se pudo cargar el servicio divino seleccionado'
                ));
                this.router.navigate(['/divine-services']);
              }
            });
        },
        error: () => {
          this.notificationService.error('No se pudieron verificar tus permisos. Intentá de nuevo más tarde.');
          this.router.navigate(['/divine-services']);
        }
      });
  }

  /**
   * Inicializa un nuevo servicio divino.
   * Carga las unidades manejables y pre-selecciona si solo hay una disponible.
   * Si el usuario no tiene ninguna unidad disponible, redirige a la lista.
   */
  private initializeNewService(): void {
    const today = new Date();
    this.serviceForm = this.formBuilderService.createServiceForm();
    this.isLoadingPermissions = true;
    this.cdr.markForCheck();

    this.divineServiceApiService.getManageableUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dtos) => {
          const manageableUnits: OrganizationUnitItem[] = dtos
            .filter(dto => dto.id != null && dto.name != null)
            .filter(dto => dto.organizationLevelId === this.COMMUNITY_LEVEL)
            .map(dto => ({
              id: dto.id!,
              name: dto.name!,
              levelId: dto.organizationLevelId ?? 0,
              levelName: dto.levelName ?? 'Sin nivel',
              parentId: dto.parentUnitId ?? undefined,
              ancestorNames: dto.ancestorNames ?? []
            }));

          if (manageableUnits.length === 0) {
            this.notificationService.error('No tenés permisos para crear servicios divinos en ninguna comunidad.');
            this.router.navigate(['/divine-services']);
            return;
          }

          this.unitSource = manageableUnits;
          this.isLoadingPermissions = false;

          this.formBuilderService.setFormEnabled(this.serviceForm, true);
          this.serviceForm.patchValue({ date: this.fromDate(today) }, { emitEvent: false });

          const routeUnit = this.findManageableRouteUnit(manageableUnits);
          const initialUnit = routeUnit ?? (manageableUnits.length === 1 ? manageableUnits[0] : null);

          if (initialUnit) {
            this.serviceForm.patchValue({ organizationUnitId: initialUnit.id }, { emitEvent: false });
            this.facade.initializeNewService(initialUnit.id);
          } else {
            this.facade.initializeNewService();
          }

          if (manageableUnits.length === 1) {
            this.serviceForm.get('organizationUnitId')?.disable();
          }

          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.error('No se pudieron verificar tus permisos. Intentá de nuevo más tarde.');
          this.router.navigate(['/divine-services']);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.facade.cleanup();
  }

  /**
   * Guarda el servicio divino (crear o actualizar)
   */
  save(): void {
    if (this.serviceForm.invalid) {
      this.serviceForm.markAllAsTouched();
      this.notificationService.warning('Por favor, completá todos los campos requeridos correctamente');
      this.focusFirstInvalidControl();
      return;
    }

    const isNewService = this.isNewService;

    this.facade.saveDivineService(this.serviceForm.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (savedService) => {
          this.cdr.markForCheck();
          this.notificationService.success('Servicio divino guardado correctamente');
          const targetUnitId = savedService.organizationUnitId || this.unitId;

          if (isNewService && targetUnitId) {
            this.router.navigate(['/units', targetUnitId, 'divine-services', savedService.id]);
            return;
          }

          if (targetUnitId) {
            this.router.navigate(['/units', targetUnitId, 'divine-services']);
          } else {
            this.router.navigate(['/divine-services']);
          }
        },
        error: (error) => {
          console.error('Error saving divine service:', error);
          this.notificationService.error(getApiErrorMessage(
            error,
            'No se pudo guardar el servicio divino. Revisá los datos cargados e intentá nuevamente'
          ));
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Retorna el título de la página
   */
  cancel(): void {
    this.breadcrumbService.back();
  }

  get pageTitle(): string {
    return this.isNewService ? 'Nuevo Servicio Divino' : 'Editar Servicio Divino';
  }

  /**
   * Maneja los cambios de visibilidad de campos opcionales
   */
  onFieldVisibilityChange(changes: Partial<any>): void {
    Object.keys(changes).forEach(field => {
      this.facade.updateFieldVisibility(field as any, changes[field]);
    });
  }

  private focusFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector(
        'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid, .mat-mdc-select.ng-invalid, [formcontrolname].ng-invalid'
      ) as HTMLElement | null;

      if (!firstInvalid) {
        return;
      }

      const focusTarget = (firstInvalid.matches('input, textarea, select')
        ? firstInvalid
        : firstInvalid.querySelector('input, textarea, button, [tabindex]')) as HTMLElement | null;

      (focusTarget ?? firstInvalid).focus();
      (focusTarget ?? firstInvalid).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private fromDateTime(value: string): any {
    return this.fromDate(new Date(value));
  }

  private fromDate(value: Date): any {
    return this.dateAdapter.createDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private findManageableRouteUnit(manageableUnits: OrganizationUnitItem[]): OrganizationUnitItem | null {
    if (!this.unitId) {
      return null;
    }

    const routeUnitId = this.unitId.toLowerCase();
    return manageableUnits.find(unit => unit.id.toLowerCase() === routeUnitId) ?? null;
  }
}
